import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTimestamp, setAccessMonitoringCameraId } from '../../api/dataSlice'
import { useGetAccessMonitoringQuery } from '../../api/graphqlApi'
import { buildLastEventByDay, databaseDateToUtc, DAY_REGEX, isSameTimestamp, normalizeToAvailableTimestamp, timestampToIso } from '../Utilities'

// shared derivation for the access monitoring controls + closed mobile footer
// called by AccessControls (in drawer) and ArchiveControls (for the closed mobile footer) so that footer data is available even when AccessControls is not mounted (mobile portrait: the accordion only mounts while the drawer is open)
// isActive gates the side effects (initial camera selection + "jump to latest event" timestamp) so they only run when access view is in use - without it, those effects would clobber the archive/live timestamp whenever /mysite renders
export function useAccessFooterState(isActive = false) {

    const { CAMERAS } = window.conf
    const { timestamp, accessMonitoringCameraId } = useSelector(getData)
    const { data: accessMonitoringData, isLoading: loading } = useGetAccessMonitoringQuery()
    
    const dispatch = useDispatch()
    const timestampRef = useRef(timestamp)

    const availableCameras = useMemo(
        () => CAMERAS
            .filter(elem => (accessMonitoringData?.byCameraId?.[elem?.id]?.length || 0) > 0)
            .map(elem => elem.id),
        [accessMonitoringData, CAMERAS]
    )

    const setSelectedCameraId = useCallback(
        (id) => dispatch(setAccessMonitoringCameraId(id === "" ? null : id)),
        [dispatch]
    )

    const activeCameraId = accessMonitoringCameraId ?? availableCameras?.[0] ?? null

    const enabledDaysForCam = useMemo(
        () => accessMonitoringData?.daysByCameraId?.[activeCameraId] || [],
        [accessMonitoringData, activeCameraId]
    )

    const { minDate, maxDate } = useMemo(() => {
        if (enabledDaysForCam.length === 0) return { minDate: null, maxDate: null }
        return {
            minDate: dayjs(enabledDaysForCam[0]),
            maxDate: dayjs(enabledDaysForCam[enabledDaysForCam.length - 1])
        }
    }, [enabledDaysForCam])

    const eventTimestamps = useMemo(() => {
        if (activeCameraId == null) return []
        const items = accessMonitoringData?.byCameraId?.[activeCameraId] || []
        const list = items
            .map(item => item?.timestamp ? databaseDateToUtc(item.timestamp) : null)
            .filter(Boolean)
        return Array.from(new Set(list)).sort((a, b) => Date.parse(a) - Date.parse(b))
    }, [accessMonitoringData, activeCameraId])

    const lastEventByDay = useMemo(
        () => buildLastEventByDay(eventTimestamps),
        [eventTimestamps]
    )

    const effectiveTimestamp = useMemo(
        () => normalizeToAvailableTimestamp(timestamp, eventTimestamps, lastEventByDay) ?? timestamp,
        [timestamp, eventTimestamps, lastEventByDay] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const currentDay = useMemo(() => {
        if (!effectiveTimestamp) return null
        if (typeof effectiveTimestamp === 'string' && DAY_REGEX.test(effectiveTimestamp)) return effectiveTimestamp
        const iso = timestampToIso(effectiveTimestamp)
        return iso ? iso.slice(0, 10) : null
    }, [effectiveTimestamp])

    const currentDayIndex = useMemo(() => {
        if (!currentDay || enabledDaysForCam.length === 0) return -1
        return enabledDaysForCam.indexOf(currentDay)
    }, [currentDay, enabledDaysForCam])

    const canStepLeft = currentDayIndex > 0
    const canStepRight = currentDayIndex >= 0 && currentDayIndex < enabledDaysForCam.length - 1

    const updateTimestamp = useCallback((value) => {
        const isoTimestamp = timestampToIso(value)
        if (!isoTimestamp) return
        const normalizedTimestamp = normalizeToAvailableTimestamp(isoTimestamp, eventTimestamps, lastEventByDay) ?? isoTimestamp
        if (isSameTimestamp(normalizedTimestamp, timestampRef.current)) return
        dispatch(setTimestamp(normalizedTimestamp))
    }, [dispatch, eventTimestamps, lastEventByDay])

    const stepLeft = useCallback(() => {
        if (!canStepLeft) return
        updateTimestamp(enabledDaysForCam[currentDayIndex - 1])
    }, [canStepLeft, updateTimestamp, enabledDaysForCam, currentDayIndex])

    const stepRight = useCallback(() => {
        if (!canStepRight) return
        updateTimestamp(enabledDaysForCam[currentDayIndex + 1])
    }, [canStepRight, updateTimestamp, enabledDaysForCam, currentDayIndex])

    const shouldDisableDate = useCallback((date) => {
        const day = date?.format?.('YYYY-MM-DD')
        return !day || !enabledDaysForCam.includes(day)
    }, [enabledDaysForCam])

    // keep latest timestamp in a ref so the "jump to latest event" effect doesn't depend on it
    useEffect(() => {
        timestampRef.current = timestamp
    }, [timestamp])

    // set initial selected camera — only when access view is active
    useEffect(() => {
        if (!isActive) return
        if (!availableCameras?.length) return
        if (accessMonitoringCameraId == null || !availableCameras.includes(accessMonitoringCameraId)) {
            setSelectedCameraId(availableCameras[0])
        }
    }, [isActive, availableCameras, accessMonitoringCameraId, setSelectedCameraId])

    // jump to latest event once camera/data is available — only when access view is active
    useEffect(() => {
        if (!isActive) return
        if (activeCameraId == null || eventTimestamps.length === 0) return

        // if current timestamp already falls on a day with data for this camera, keep it
        if (currentDay && enabledDaysForCam.includes(currentDay)) return

        const lastEvent = eventTimestamps[eventTimestamps.length - 1]
        if (!lastEvent) return
        if (!isSameTimestamp(lastEvent, timestampRef.current)) {
            updateTimestamp(lastEvent)
        }
    }, [isActive, activeCameraId, eventTimestamps]) // eslint-disable-line react-hooks/exhaustive-deps

    return {
        loading,
        availableCameras,
        activeCameraId,
        setSelectedCameraId,
        enabledDaysForCam,
        minDate,
        maxDate,
        effectiveTimestamp,
        currentDay,
        currentDayIndex,
        canStepLeft,
        canStepRight,
        updateTimestamp,
        stepLeft,
        stepRight,
        shouldDisableDate
    }
}
